import type { ReactNode } from 'react';
import { useI18n } from '@/ui/i18n/I18nProvider';
import styles from './Page.module.css';
import { useBack } from '@/ui/back/backStack';
import { Icon } from './Icon';

/**
 * Full-screen page on phones, centred modal on desktop (S09/S17/S18, ux-screens §2.2).
 * Esc or ← goes back.
 */
export function Page({
  title,
  onBack,
  aside,
  children,
  testId,
}: {
  title: string;
  onBack: () => void;
  aside?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  const { t } = useI18n();
  useBack(onBack);
  return (
    <div className={styles.scrim}>
      <section
        className={styles.page}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
      >
        <header className={styles.header}>
          <button type="button" className={styles.back} aria-label={t('ui.common.back')} onClick={onBack}>
            <Icon name="back" size={26} />
          </button>
          <h2 className={styles.title}>{title}</h2>
          {aside}
        </header>
        <div className={styles.body}>{children}</div>
      </section>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

export function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowText}>
        <span>{label}</span>
        {hint && <small>{hint}</small>}
      </div>
      <div className={styles.rowControl}>{children}</div>
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={styles.switch}
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className={styles.knob} />
    </button>
  );
}

export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div className={styles.segmented} role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={styles.segment}
          onClick={() => {
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
